import type { WhatsNewAnnouncement } from 'src/services/api/whatsNewApi';
import { DateUtils } from 'src/utils';

// Keeps the modal out of the way while a new workspace is still onboarding.
export const MIN_WORKSPACE_AGE_HOURS = 3 * 24;

interface SelectWhatsNewAnnouncementInput {
  announcements: WhatsNewAnnouncement[];
  isAdmin: boolean;
}

/**
 * The single announcement the user is eligible for: the most recent one,
 * skipping admin-only entries for non-admins so they fall through to the
 * latest announcement meant for everyone.
 */
export const selectWhatsNewAnnouncement = ({
  announcements,
  isAdmin,
}: SelectWhatsNewAnnouncementInput): WhatsNewAnnouncement | undefined => {
  const eligible = announcements.filter(
    (announcement) => isAdmin || !announcement.adminsOnly,
  );
  return [...eligible]
    .sort((a, b) => Date.parse(b.publishDate) - Date.parse(a.publishDate))
    .at(0);
};

interface ShouldShowWhatsNewInput {
  announcementId: string;
  dismissedId: string | undefined;
  workspaceCreatedAt: Date | undefined;
  isForcedOpen: boolean;
}

/**
 * Whether the selected announcement should open: not already dismissed and
 * the workspace is past onboarding. A forced open bypasses both checks.
 */
export const shouldShowWhatsNew = ({
  announcementId,
  dismissedId,
  workspaceCreatedAt,
  isForcedOpen,
}: ShouldShowWhatsNewInput): boolean => {
  if (isForcedOpen) return true;
  if (dismissedId === announcementId) return false;
  return (
    !!workspaceCreatedAt &&
    DateUtils.getDiffFromDate(new Date(workspaceCreatedAt), 'hours') >=
      MIN_WORKSPACE_AGE_HOURS
  );
};
